import { AnalyticsBrowser } from '@segment/analytics-next';
import isNil from 'lodash/isNil';
import { AnalyticsEvents } from '~/services/analytics';
import { Company } from '~/types/company';
import { Subscription } from '~/types/subscriptions';
import { User, UserWithCompanies } from '~/types/user';
import { getCachedCompanyUserFeatures } from '~/utils/companyUserCache';

export type EventProps = Record<string, unknown>;

const print = (kind: string) => {
  // eslint-disable-next-line no-console
  return (...args: unknown[]) => console.info('analytics', kind, ...args);
};

const analytics: AnalyticsBrowser =
  import.meta.env.MODE === 'test'
    ? ({
        identify: () => {},
        group: () => {},
        track: () => {},
        page: () => {},
      } as unknown as AnalyticsBrowser)
    : import.meta.env.DEV
      ? ({
          identify: print('identify'),
          group: print('group'),
          track: print('track'),
          page: print('page'),
        } as AnalyticsBrowser)
      : AnalyticsBrowser.load(
          { writeKey: import.meta.env.VITE_SEGMENT_APP_KEY, cdnURL: import.meta.env.VITE_SEGMENT_PROXY },
          { obfuscate: true, disable: import.meta.env.DEV },
        );

export const identify = (user?: UserWithCompanies) => {
  if (user) {
    analytics.identify(user.id, {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone,
      createdAt: user.createdAt,
      companies: user.companies.map((company) => ({
        company_id: company.id,
        name: company.name,
      })),
    });
  }
};

export const group = (company: Company, subscription?: Subscription) => {
  analytics.group(company.id, {
    id: company.id,
    name: company.name,
    subscriptionStatus: subscription?.status,
    website: `https://${company.id}.com`,
  });
};

export const track = (
  eventName: AnalyticsEvents,
  user?: User,
  company?: Company | null,
  additionalProperties?: EventProps,
) => {
  if (process.env.NODE_ENV === 'test' && isNil(analytics)) return;
  analytics.track(eventName, {
    userId: user?.id,
    email: user?.email,
    firstName: user?.firstName,
    lastName: user?.lastName,
    phone: user?.phone,
    companyId: company?.id,
    companyName: company?.name,
    companyFeatures: company?.featuresV2?.map((feature) => feature.value),
    userFeatures: user && company ? getCachedCompanyUserFeatures(company.id, user.id) : undefined,
    ...additionalProperties,
  });
};

export const page = (user?: User, company?: Company | null, additionalProperties?: EventProps) => {
  analytics.page(undefined, undefined, {
    email: user?.email,
    firstName: user?.firstName,
    lastName: user?.lastName,
    phone: user?.phone,
    companyId: company?.id,
    companyName: company?.name,
    companyFeatures: company?.featuresV2?.map((feature) => feature.value),
    userFeatures: user && company ? getCachedCompanyUserFeatures(company.id, user.id) : undefined,
    ...additionalProperties,
  });
};
